import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import EmptyData from '../../../../common/emptyData/EmptyData';
import GenericTableView from './GenericTableView';
import { buildAnnotationMap, createFieldFormatter } from '../../../../../helpers/fieldValueFormatter';
import useFieldLabelsAPI from '../../../../../hooks/useFieldLabelsAPI';

function GenericGroupedView({
  resultSet,
  reportType,
  formatting,
  onDrillDown,
  groupByDimension: savedGroupByDimension,
  fieldAliasMap = {},
  linkHiddenFields = [],
}) {
  const intl = useIntl();
  const { labelsMap: dimLabels } = useFieldLabelsAPI({ fieldType: 'dimension' });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const resolveLabel = (key) => {
    const parts = key.split('_');
    for (let i = 1; i < parts.length; i++) {
      const candidate = parts.slice(0, i).join('_') + '.' + parts.slice(i).join('_');
      const alias = fieldAliasMap?.[candidate];
      if (alias && labelsMap?.[alias]) return labelsMap[alias];
      if (labelsMap?.[candidate]) return labelsMap[candidate];
    }
    if (labelsMap?.[key]) return labelsMap[key];
    return key.split('_').pop().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const annotationMap = useMemo(() => buildAnnotationMap(resultSet), [resultSet]);

  // Detect available dimensions from data
  const availableDimensions = useMemo(() => {
    if (!resultSet?.loadResponses?.length) return [];
    const data = resultSet.loadResponses[0]?.data || [];
    if (!data.length) return [];
    const allKeys = Object.keys(data[0]);
    const measureKeys = allKeys.filter(k => {
      const val = data[0][k];
      return val !== null && val !== undefined && !isNaN(Number(val)) && String(val).trim() !== '';
    });
    return allKeys
      .filter(k => !measureKeys.includes(k))
      .filter(k => !linkHiddenFields.includes(k));
  }, [resultSet, linkHiddenFields]);

  // Runtime selection — default to saved value or first dimension
  const defaultDim = savedGroupByDimension || availableDimensions[0] || '';
  const [activeGroupDim, setActiveGroupDim] = useState(defaultDim);

  // Update if savedGroupByDimension changes (e.g. different report loaded)
  const effectiveGroupDim = activeGroupDim || availableDimensions[0] || '';

  const groups = useMemo(() => {
    if (!resultSet?.loadResponses?.length || !effectiveGroupDim) return [];
    const data = resultSet.loadResponses[0]?.data || [];
    if (!data.length) return [];

    const groupDimNorm = effectiveGroupDim.replace('.', '_');
    const groupRefFormatter = createFieldFormatter(groupDimNorm, annotationMap, { intl });
    const translateLabel = (val) => {
      if (!val || val === '—') return val;
      const fakeItem = { [groupDimNorm]: val };
      return (groupRefFormatter ? groupRefFormatter(fakeItem) : null) || val;
    };

    const groupMap = {};
    const groupOrder = [];
    data.forEach(row => {
      const gVal = row[effectiveGroupDim] != null ? String(row[effectiveGroupDim]) : '—';
      if (!groupMap[gVal]) { groupMap[gVal] = []; groupOrder.push(gVal); }
      groupMap[gVal].push(row);
    });

    return groupOrder.map(gVal => ({
      label: translateLabel(gVal),
      subResultSet: {
        loadResponses: [{ data: groupMap[gVal], total: groupMap[gVal].length, annotation: resultSet.loadResponses[0]?.annotation }],
        rawData: () => groupMap[gVal],
        tablePivot: () => groupMap[gVal],
        chartPivot: () => [],
        series: () => [],
        seriesNames: () => [],
        totalRow: () => ({}),
        annotation: () => ({}),
      },
      hiddenFields: [groupDimNorm, ...linkHiddenFields],
    }));
  }, [resultSet, effectiveGroupDim, intl]);

  if (!availableDimensions.length) return <EmptyData />;

  const dimOptions = availableDimensions.map(dim => {
    const dimNorm = dim.replace('.', '_');
    return { value: dim, label: resolveLabel(dimNorm) };
  });

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      {/* Runtime dimension selector */}
      <div style={{ padding: '0.5rem 1rem 0', maxWidth: '320px' }}>
        <Select
          label={
            <FormattedMessage
              id="ui-reports.visualization.groupByDimension.label"
              defaultMessage="Group by"
            />
          }
          value={effectiveGroupDim}
          dataOptions={dimOptions}
          onChange={e => setActiveGroupDim(e.target.value)}
          marginBottom0
        />
      </div>

      <div style={{ padding: '0 1rem', marginTop: '1rem' }}>
        {groups.length === 0
          ? <EmptyData />
          : groups.map((group, idx) => (
            <div key={idx} style={{ marginBottom: '2rem' }}>
              <div style={{
                padding: '0.5rem 0.75rem',
                marginBottom: '0.5rem',
                background: 'var(--color-fill-accent-secondary, #e8f0fe)',
                borderInlineStart: '4px solid var(--color-brand-primary, #1F4E79)',
                borderRadius: '0 4px 4px 0',
                fontWeight: '600',
                fontSize: '0.95rem',
                color: 'var(--color-text-primary, #1F4E79)',
              }}>
                {group.label}
              </div>
              <GenericTableView
                resultSet={group.subResultSet}
                reportType={reportType}
                formatting={formatting}
                onDrillDown={onDrillDown}
                hiddenFields={group.hiddenFields}
                fieldAliasMap={fieldAliasMap}
                displaySettings={{ autosize: false, maxHeight: 400 }}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
}

GenericGroupedView.propTypes = {
  resultSet:        PropTypes.object.isRequired,
  reportType:       PropTypes.string,
  formatting:       PropTypes.object,
  onDrillDown:      PropTypes.func,
  groupByDimension: PropTypes.string,
  fieldAliasMap:    PropTypes.object,
  linkHiddenFields: PropTypes.arrayOf(PropTypes.string),
};

export default GenericGroupedView;
